import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchWarehouseList } from "../../store/slices/warehouseSlice";
import { fetchProductsByWarehousePagination } from "../../store/slices/productSlice";
import Loading from "../../components/AssetsComponent/loading";

const ProductWarehouseStock: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const hasAutoSelected = useRef(false);
  const hasFetchedWarehouses = useRef(false);
  const lastFetchRef = useRef<string>("");

  const warehouseNames = useSelector(
    (state: RootState) => state.warehouse.warehouses || [],
  );
  const products = useSelector(
    (state: RootState) => state.product.warehouseProducts || [],
  );
  const totalPages = useSelector(
    (state: RootState) => state.product.totalWarehousePages,
  );
  const currentPage = useSelector(
    (state: RootState) => state.product.currentWarehousePage,
  );
  const loading = useSelector(
    (state: RootState) => state.product.warehouseLoading,
  );

  const assignedWarehouseId = useSelector(
    (state: RootState) => state.auth.userName?.warehouseAssigned,
  );

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Sorting State
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "desc" });

  const handleSortRequest = (key: string) => {
    let direction: "asc" | "desc" = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
    setPage(1);
    lastFetchRef.current = "";
  };

  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key)
      return <span className="ml-1 text-gray-400">↕</span>;
    return sortConfig.direction === "asc" ? (
      <span className="ml-1 text-blue-600">↑</span>
    ) : (
      <span className="ml-1 text-blue-600">↓</span>
    );
  };

  useEffect(() => {
    if (!hasFetchedWarehouses.current) {
      dispatch(fetchWarehouseList());
      hasFetchedWarehouses.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      assignedWarehouseId &&
      warehouseNames.length > 0 &&
      !selectedWarehouseId &&
      !hasAutoSelected.current
    ) {
      const wh = warehouseNames.find((w) => w._id === assignedWarehouseId);
      if (wh) {
        setSelectedWarehouseId(wh._id);
        hasAutoSelected.current = true;
      }
    }
  }, [warehouseNames, assignedWarehouseId, selectedWarehouseId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (selectedWarehouseId) {
      const fetchKey = `${selectedWarehouseId}-${page}-${debouncedSearch}-${sortConfig.key}-${sortConfig.direction}`;

      if (lastFetchRef.current !== fetchKey) {
        lastFetchRef.current = fetchKey;

        dispatch(
          fetchProductsByWarehousePagination({
            warehouseId: selectedWarehouseId,
            page,
            limit: 10,
            search: debouncedSearch,
            sortBy: sortConfig.key || "",
            sortOrder: sortConfig.direction,
          }),
        );
      }
    }
  }, [selectedWarehouseId, page, debouncedSearch, sortConfig, dispatch]);

  const selectedWarehouse = warehouseNames.find(
    (w) => w._id === selectedWarehouseId,
  );

  const quantityBadge = (qty: number) => {
    const base = "px-2 py-1 text-xs font-semibold rounded-full";
    if (qty > 50) return `${base} bg-green-100 text-green-800`;
    if (qty >= 15) return `${base} bg-yellow-100 text-yellow-800`;
    return `${base} bg-red-100 text-red-800`;
  };

  const handleWarehouseChange = useCallback(
    (opt: { value: string; label: string } | null) => {
      setSelectedWarehouseId(opt?.value || null);
      setPage(1);
      hasAutoSelected.current = true;
      lastFetchRef.current = "";
    },
    [],
  );

  const handlePrevPage = useCallback(() => {
    setPage((prev) => Math.max(1, prev - 1));
    lastFetchRef.current = "";
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((prev) => Math.min(totalPages, prev + 1));
    lastFetchRef.current = "";
  }, [totalPages]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      lastFetchRef.current = "";
    },
    [],
  );

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-800 flex items-center">
        <span className="w-1 h-6 bg-navyBlue-primary mr-3 rounded-full" />
        Warehouse Stock by Product
      </h2>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 max-w-64">
          <select
            value={selectedWarehouse?._id || ""}
            onChange={(e) => {
              const selected = warehouseNames.find(
                (w) => w._id === e.target.value,
              );
              handleWarehouseChange(
                selected
                  ? { value: selected._id, label: selected.name || "" }
                  : null,
              );
            }}
            className="w-full p-2 border rounded focus:outline-none focus:ring focus:ring-blue-200 text-sm"
          >
            <option value="">Select Warehouse...</option>
            {warehouseNames.map((w) => (
              <option key={w._id} value={w._id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <input
          type="text"
          placeholder="Search product..."
          value={search}
          onChange={handleSearchChange}
          className="p-2 border rounded flex-shrink md:w-64 min-w-3"
        />
      </div>

      {selectedWarehouseId ? (
        loading ? (
          <Loading />
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSortRequest("qty")}
                    >
                      Quantity {renderSortIcon("qty")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      HSN
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSortRequest("price")}
                    >
                      Price {renderSortIcon("price")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Range
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.length > 0 ? (
                    products.map((p) => (
                      <tr key={p._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {p.productName}
                        </td>
                        <td className="px-4 py-3">
                          <span className={quantityBadge(p.quantity)}>
                            {p.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {p.hsn}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          ₹{p.price}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {p.range?.startData && p.range?.endData
                            ? `${p.range.startData} - ${p.range.endData}`
                            : "—"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No products found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 0 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    page === 1
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-[#0077C0] text-white hover:bg-[#005B90]"
                  }`}
                >
                  Prev
                </button>
                <span className="px-4 py-2 text-gray-700 font-medium">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    page === totalPages
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-[#0077C0] text-white hover:bg-[#005B90]"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )
      ) : (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border">
          Select a warehouse to view stock
        </div>
      )}
    </div>
  );
};

export default ProductWarehouseStock;
